import React, { useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { logFoodAPI } from '../../../utils/api';
import { scanBarcode } from '../../../utils/fitxessBarcodeScanner';
import RecentlyLoggedList from '../../../components/logfood/RecentlyLoggedList';
import FatSecretAttribution from '../../../components/logfood/FatSecretAttribution';
import { searchItemToFood } from '../../../utils/logFoodMappers';

/**
 * Barcode tab — FatSecret UPC lookup.
 *
 * Landing shows: big Scan CTA + manual UPC entry + recently scanned items.
 * Scanner is INTENTIONALLY not auto-opened (user spec). Tap the CTA →
 * native AVFoundation scanner → resolves a UPC → /barcode/lookup.
 *
 * On non-iOS platforms (web preview, Android, simulator without camera),
 * `scanBarcode()` returns null and the user is guided to manual UPC entry.
 */
export default function BarcodeTab(props) {
  const recents = props.recents;
  const recentsLoading = props.recentsLoading;
  const onSelectFood = props.onSelectFood;
  const onSelectRecent = props.onSelectRecent;
  const onQuickLog = props.onQuickLog;
  const onQuickLogRecent = props.onQuickLogRecent;
  const onCreateManual = props.onCreateManual;

  const [upc, setUpc] = useState('');
  const [looking, setLooking] = useState(false);
  const [scanning, setScanning] = useState(false);

  // Shared lookup → routes to FoodConfirm on found, to manual entry w/
  // UPC prefill when FatSecret says not found, to a toast on transport errors.
  const lookupUpc = function (rawUpc) {
    const trimmed = (rawUpc || '').trim();
    if (!trimmed) return;
    setLooking(true);
    logFoodAPI.lookupBarcode(trimmed)
      .then(function (res) {
        setLooking(false);
        const data = res.data || {};
        if (data.found && data.item) {
          onSelectFood(searchItemToFood(data.item));
        } else if (data.fallback_suggestion) {
          toast(data.fallback_suggestion.message || 'Product not found', {
            action: {
              label: 'Add manually',
              onClick: function () {
                onCreateManual && onCreateManual('UPC ' + trimmed);
              },
            },
          });
        } else {
          toast.error('Product not found.');
        }
      })
      .catch(function (err) {
        setLooking(false);
        const status = err && err.response && err.response.status;
        const detail = (err && err.response && err.response.data && err.response.data.detail) || '';
        console.error('[BarcodeTab] lookupBarcode failed:', { status, detail, message: err && err.message });
        if (status === 503) {
          // Backend FatSecret integration is gated off — surface a clear
          // message but keep the captured UPC for when keys arrive.
          toast.error('Barcode lookup is offline.');
        } else if (status === 401) {
          toast.error('Session expired — please sign in again.');
        } else if (status) {
          toast.error('Lookup failed (HTTP ' + status + ').');
        } else {
          toast.error('Lookup failed — network unreachable.');
        }
      });
  };

  const handleScan = async function () {
    if (scanning) return;
    setScanning(true);
    try {
      const result = await scanBarcode();
      setScanning(false);
      if (!result) {
        // Native plugin unavailable. On a real iOS device this means the
        // FitxessBarcodeScanner Swift files were not added to the Xcode
        // target — fall back to manual UPC entry so the feature still
        // works while the native build is fixed.
        toast.info('Scanner unavailable on this build. Enter the UPC below to look it up.');
        return;
      }
      if (result.cancelled) {
        return;
      }
      if (result.upc) {
        setUpc(result.upc);
        lookupUpc(result.upc);
      }
    } catch (err) {
      setScanning(false);
      const code = (err && (err.code || err.message)) || '';
      if (code === 'PERMISSION_DENIED' || String(code).includes('PERMISSION_DENIED')) {
        toast.error('Camera permission denied. Enable it in Settings to scan.');
      } else if (code === 'NO_CAMERA' || String(code).includes('NO_CAMERA')) {
        toast.error('No camera available on this device.');
      } else {
        toast.error('Scanner failed to start.');
      }
    }
  };

  return (
    <div className="flex flex-col" data-testid="barcode-tab">
      <div className="px-4 py-6">
        <button
          type="button"
          onClick={handleScan}
          disabled={scanning}
          className="w-full rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors py-10 flex flex-col items-center gap-3 disabled:opacity-60"
          data-testid="barcode-scan-cta"
        >
          {scanning ? (
            <Loader2 className="size-10 text-primary animate-spin" />
          ) : (
            <Camera className="size-10 text-primary" />
          )}
          <span className="text-lg font-semibold text-foreground">
            {scanning ? 'Opening scanner…' : 'Scan Barcode'}
          </span>
          <span className="text-xs text-muted-foreground text-center px-6">
            Point your camera at a UPC to log it instantly
          </span>
        </button>
      </div>

      <div className="px-4 py-2 space-y-2">
        <label className="block text-sm text-muted-foreground">Or enter UPC manually:</label>
        <div className="flex gap-2">
          <input
            type="text"
            inputMode="numeric"
            value={upc}
            onChange={function (e) { setUpc(e.target.value.replace(/\D/g, '')); }}
            placeholder="0 1 2 3 4 5 6 7 8 9 0 1 2"
            className="flex-1 px-3 py-2 rounded-md border border-input bg-background text-sm font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="barcode-upc-input"
          />
          <Button
            onClick={function () { lookupUpc(upc); }}
            disabled={!upc.trim() || looking}
            data-testid="barcode-lookup-btn"
          >
            {looking ? <Loader2 className="size-4 animate-spin" /> : 'Look Up'}
          </Button>
        </div>
      </div>

      <RecentlyLoggedList
        items={recents}
        onSelect={onSelectRecent}
        onQuickLog={onQuickLogRecent}
        title="Recently Scanned"
        emptyMessage={recentsLoading ? 'Loading…' : null}
      />

      <FatSecretAttribution />
    </div>
  );
}
